/**
 * PDP Gallery — Flickity-powered product media gallery
 *
 * Custom element <pdp-gallery> that initializes Flickity carousels
 * for main viewer, thumbnails, and lightbox.
 *
 * Supports two modes:
 * - "standard": All product.media shown (replaces Dawn's slider-component)
 * - "per-color": Only metaobject images for the selected color
 */

if (!customElements.get('pdp-gallery')) {
  customElements.define(
    'pdp-gallery',
    class PdpGallery extends HTMLElement {
      constructor() {
        super();
        this.mainFlkty = null;
        this.thumbFlkty = null;
        this.lightboxFlkty = null;

        this.galleryMode = this.dataset.galleryMode || 'standard';
        this.currentColorId = this.dataset.currentColorId || '';
        this.sectionId = this.dataset.sectionId || '';
        this.mobileLayout = this.dataset.mobileLayout || '';
      }

      connectedCallback() {
        // Wait for Flickity to be available
        if (typeof Flickity === 'undefined') {
          this._onLoad = () => this.init();
          window.addEventListener('load', this._onLoad);
          return;
        }
        this.init();
      }

      init() {
        const mainEl = this.querySelector('.pdp-gallery__main');
        const thumbEl = this.querySelector('.pdp-gallery__thumbnails');
        if (mainEl) mainEl.classList.remove('hidden');
        if (thumbEl) thumbEl.classList.remove('hidden');

        this.initMain();
        this.initThumbnails();
        this.bindEvents();
        this.reconcileCellsOnLoad();

        const skeleton = this.querySelector('.pdp-gallery__skeleton');
        const skeletonThumbs = this.querySelector('.pdp-gallery__skeleton-thumbs');
        if (skeleton) skeleton.classList.add('hidden');
        if (skeletonThumbs) skeletonThumbs.classList.add('hidden');
      }

      /**
       * If Flickity upgraded before the trailing server-rendered extra-media
       * slides were parsed, it counts too few cells (the extras sit stacked at
       * left:0, unreachable). Once the document is fully loaded, re-scan cells
       * and re-layout main + thumbnails so all slides are registered.
       */
      reconcileCellsOnLoad() {
        const run = () => {
          this._absorbOrphanSlides(this.mainFlkty, this.querySelector('.pdp-gallery__main'), '.pdp-gallery__slide');
          this._absorbOrphanSlides(this.thumbFlkty, this.querySelector('.pdp-gallery__thumbnails'), '.pdp-gallery__thumb');
          this.updateArrows();
        };
        if (document.readyState === 'complete') {
          run();
        } else {
          this._reconcileCells = run;
          window.addEventListener('load', this._reconcileCells, { once: true });
        }
      }

      /**
       * Flickity moves its cells into an inner `.flickity-slider` at init. Trailing
       * extra-media slides parsed after init stay as direct children of the
       * container (siblings of the slider) — Flickity never registers them and
       * reloadCells() can't reach them. Move such orphans into Flickity as cells.
       */
      _absorbOrphanSlides(flkty, containerEl, slideSelector) {
        if (!flkty || !containerEl) return;
        const slider = containerEl.querySelector('.flickity-slider');
        if (!slider) return;
        const orphans = [...containerEl.children].filter(
          (c) => c !== slider && c.matches && c.matches(slideSelector)
        );
        if (!orphans.length) return;
        flkty.append(orphans);
        flkty.resize();
      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        if (this._onLoad) {
          window.removeEventListener('load', this._onLoad);
          this._onLoad = null;
        }
        this._destroyFlickityInstances();
        // Return lightbox from body if it was moved there
        const bodyLightbox = document.body.querySelector('.pdp-lightbox');
        if (bodyLightbox && this._lightboxPlaceholder && this._lightboxPlaceholder.parentNode) {
          this._lightboxPlaceholder.parentNode.insertBefore(bodyLightbox, this._lightboxPlaceholder);
          this._lightboxPlaceholder.remove();
        }
        this.removeEventListeners();
      }

      _destroyFlickityInstances() {
        if (this.mainFlkty) { this.mainFlkty.destroy(); this.mainFlkty = null; }
        if (this.thumbFlkty) { this.thumbFlkty.destroy(); this.thumbFlkty = null; }
        if (this.lightboxFlkty) { this.lightboxFlkty.destroy(); this.lightboxFlkty = null; }
      }

      /**
       * Re-initialize the main + thumbnail carousels so they re-measure against
       * the new viewport. Flickity's own resize/reloadCells doesn't reliably
       * rebuild the thumbnail track on width changes (it can stay viewport-wide
       * and undraggable until a reload), so we destroy and create fresh instances.
       */
      relayout() {
        if (!this.mainFlkty && !this.thumbFlkty) return;
        const selected = this.mainFlkty ? this.mainFlkty.selectedIndex : 0;
        this._destroyFlickityInstances();
        this.initMain();
        this.initThumbnails();
        if (this.mainFlkty && selected > 0) this.mainFlkty.select(selected, false, true);
      }

      /* ============================
         Main Carousel
         ============================ */

      initMain() {
        const mainEl = this.querySelector('.pdp-gallery__main');
        if (!mainEl || mainEl.children.length === 0) return;

        const useDots = this.mobileLayout === 'dots';

        this.mainFlkty = new Flickity(mainEl, {
          cellAlign: 'left',
          contain: true,
          prevNextButtons: true,
          pageDots: useDots,
          wrapAround: false,
          draggable: true,
          imagesLoaded: true,
          adaptiveHeight: false,
          accessibility: true,
          setGallerySize: true,
        });

        // Initial arrow + overlay state
        this.updateArrows();
        this.updateOverlay(0);

        this.mainFlkty.on('change', (index) => {
          this.updateArrows();
          this.updateOverlay(index);
          this.updateGalleryStatus(index);
          this.stopExtraMediaVideos();
          this.autoplaySelectedExtraMedia();
        });
      }

      /**
       * Reset any A/B extra-media video that is no longer on the active slide:
       * remove the loaded <video> and the `loaded` attribute so Dawn restores the
       * poster (with its play button). Re-selecting the slide then shows the play
       * button again, and a fresh video loads on the next tap/click.
       */
      stopExtraMediaVideos() {
        this.querySelectorAll(
          '.pdp-gallery__main .pdp-gallery__slide:not(.is-selected) deferred-media[data-em-video]'
        ).forEach((dm) => {
          const video = dm.querySelector('video');
          if (video) {
            video.pause();
            if (video._emIO) video._emIO.disconnect();
            video.remove();
          }
          dm.removeAttribute('loaded');
          dm.classList.remove('is-playing');
        });
      }

      /**
       * Autoplay the A/B extra-media video on the active slide as soon as it is
       * selected. Browsers block sound on auto-started playback, so we start it
       * MUTED (always permitted) and flag it; the document-level gesture handler
       * (bindEvents) unmutes it during the same click/tap that selected the slide,
       * which the browser allows for an already-playing video.
       */
      autoplaySelectedExtraMedia() {
        const slide = this.mainFlkty && this.mainFlkty.selectedElement;
        const dm = slide
          ? slide.querySelector('deferred-media[data-em-video]')
          : this.querySelector(
              '.pdp-gallery__main .pdp-gallery__slide.is-selected deferred-media[data-em-video]'
            );
        this.playExtraMedia(dm);
      }

      /**
       * Load + start a single A/B extra-media video MUTED (always permitted). The
       * document gesture handler unmutes it during the same click/tap. Shared by
       * the inline gallery and the lightbox.
       */
      playExtraMedia(dm, restart) {
        if (!dm) return;
        // Already-playing video (e.g. a resize relayout re-fires the slide-change):
        // leave its play/mute state untouched so we don't re-mute an unmuted video.
        const existing = dm.querySelector('video');
        if (existing && !existing.paused && !restart) return;
        if (typeof dm.loadContent === 'function') dm.loadContent(false);
        const video = dm.querySelector('video');
        if (!video) return;
        if (restart) {
          try {
            video.currentTime = 0;
          } catch (e) {
            /* not seekable yet — fresh load already starts at 0 */
          }
        }
        video.muted = true;
        video._emAutoMuted = true;
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }

      /**
       * Mirror the inline autoplay inside the lightbox: play the video on the
       * selected lightbox slide, pause any other lightbox videos.
       */
      autoplayLightboxExtraMedia() {
        if (!this.lightboxFlkty) return;
        const selected = this.lightboxFlkty.selectedElement;
        const lightbox = this._getLightbox();
        if (lightbox) {
          lightbox.querySelectorAll('.pdp-lightbox__slide deferred-media[data-em-video] video').forEach((v) => {
            if (!selected || !selected.contains(v)) v.pause();
          });
        }
        const dm = selected && selected.querySelector('deferred-media[data-em-video]');
        this.playExtraMedia(dm, true); // lightbox always plays from the start
      }

      updateArrows() {
        if (!this.mainFlkty) return;
        const index = this.mainFlkty.selectedIndex;
        const prevBtn = this.querySelector('.pdp-gallery__main .flickity-prev-next-button.previous');
        const nextBtn = this.querySelector('.pdp-gallery__main .flickity-prev-next-button.next');

        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index >= this.mainFlkty.slides.length - 1;
      }

      updateOverlay(index) {
        const overlays = this.querySelectorAll('.pdp-gallery__main [data-overlay]');
        overlays.forEach((el, i) => {
          el.classList.toggle('is-visible', i === index);
        });
      }

      updateGalleryStatus(index) {
        const statusEl = this.querySelector(`#GalleryStatus-${this.sectionId}`);
        if (!statusEl || !this.mainFlkty) return;
        statusEl.textContent = `Image ${index + 1} of ${this.mainFlkty.slides.length}`;
      }

      /* ============================
         Thumbnails
         ============================ */

      initThumbnails() {
        const thumbEl = this.querySelector('.pdp-gallery__thumbnails');
        if (!thumbEl || thumbEl.children.length <= 1) return;

        const mainSelector = `#${CSS.escape('PdpGalleryMain-' + this.sectionId)}`;

        this.thumbFlkty = new Flickity(thumbEl, {
          asNavFor: mainSelector,
          contain: true,
          pageDots: false,
          prevNextButtons: false,
          cellAlign: 'left',
          draggable: true,
        });
      }

      /* ============================
         Lightbox
         ============================ */

      openLightbox(index) {
        const lightbox = this.querySelector('.pdp-lightbox') || document.body.querySelector('.pdp-lightbox');
        if (!lightbox) return;

        // Pause inline gallery videos so their audio doesn't play behind the modal.
        this.querySelectorAll('.pdp-gallery__main video').forEach((v) => v.pause());

        // Move lightbox to body to escape any stacking context (scroll-trigger transforms, etc.)
        if (lightbox.parentElement !== document.body) {
          this._lightboxPlaceholder = document.createComment('pdp-lightbox-placeholder');
          lightbox.parentElement.insertBefore(this._lightboxPlaceholder, lightbox);
          document.body.appendChild(lightbox);
        }

        lightbox.style.display = '';
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pdp-lightbox-open');

        const carouselEl = lightbox.querySelector('.pdp-lightbox__carousel');
        if (!carouselEl || carouselEl.children.length === 0) return;

        if (!this.lightboxFlkty) {
          this.lightboxFlkty = new Flickity(carouselEl, {
            wrapAround: true,
            prevNextButtons: true,
            pageDots: false,
            draggable: true,
            imagesLoaded: true,
            initialIndex: index || 0,
            setGallerySize: true,
          });
          // Autoplay the video when navigating to it within the lightbox.
          this.lightboxFlkty.on('change', () => this.autoplayLightboxExtraMedia());
        } else {
          this.lightboxFlkty.select(index || 0, false, true);
          this.lightboxFlkty.resize();
        }

        // Autoplay the extra-media video if the opened slide is the video.
        this.autoplayLightboxExtraMedia();

        // Focus trap + keyboard navigation
        lightbox.focus();
        this._onTrapFocus = (e) => this._trapFocus(e);
        document.addEventListener('keydown', this._onTrapFocus);
      }

      closeLightbox() {
        const lightbox = document.body.querySelector('.pdp-lightbox') || this.querySelector('.pdp-lightbox');
        if (!lightbox) return;

        // Pause any playing lightbox video (avoids audio continuing after close).
        lightbox.querySelectorAll('video').forEach((v) => v.pause());

        lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pdp-lightbox-open');

        if (this._onTrapFocus) {
          document.removeEventListener('keydown', this._onTrapFocus);
          this._onTrapFocus = null;
        }

        // Return focus to the zoom button that opened the lightbox
        const activeOverlay = this.querySelector('.pdp-gallery__overlay.is-visible');
        if (activeOverlay) {
          const zoomBtn = activeOverlay.querySelector('.pdp-gallery__zoom-btn');
          if (zoomBtn) zoomBtn.focus();
        }

        // Resume the inline gallery video on the active slide after the modal closes.
        const inlineDm =
          (this.mainFlkty && this.mainFlkty.selectedElement
            ? this.mainFlkty.selectedElement.querySelector('deferred-media[data-em-video]')
            : null) ||
          this.querySelector('.pdp-gallery__main .pdp-gallery__slide.is-selected deferred-media[data-em-video]');
        if (inlineDm) {
          const inlineVideo = inlineDm.querySelector('video');
          if (inlineVideo) {
            try {
              inlineVideo.currentTime = 0;
            } catch (e) {
              /* not seekable yet */
            }
            const resumed = inlineVideo.play();
            if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
          } else {
            this.playExtraMedia(inlineDm, true);
          }
        }
      }

      /* ============================
         Events
         ============================ */

      _getLightbox() {
        return this.querySelector('.pdp-lightbox') || document.body.querySelector('.pdp-lightbox');
      }

      _trapFocus(e) {
        if (e.key !== 'Tab') return;
        const lightbox = this._getLightbox();
        if (!lightbox) return;

        const focusable = lightbox.querySelectorAll('button:not([disabled])');
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      bindEvents() {
        // Zoom button clicks -> open lightbox
        this._onZoomClick = (e) => {
          const btn = e.target.closest('.pdp-gallery__zoom-btn');
          if (!btn) return;
          const slideIndex = parseInt(btn.dataset.slideIndex, 10) || 0;
          this.openLightbox(slideIndex);
        };
        this.addEventListener('click', this._onZoomClick);

        // Lightbox close via close button, backdrop, or clicking outside the image
        // Listen on document because lightbox is moved to body
        this._onLightboxClose = (e) => {
          const lightbox = this._getLightbox();
          if (!lightbox || lightbox.getAttribute('aria-hidden') !== 'false') return;

          // Close button or backdrop clicked
          if (e.target.closest('[data-lightbox-close]')) {
            this.closeLightbox();
            return;
          }

          // Click inside lightbox but NOT on image, arrows, or dots — close
          if (
            e.target.closest('.pdp-lightbox') &&
            !e.target.closest('.pdp-lightbox__slide img') &&
            !e.target.closest('.pdp-lightbox__slide--video') &&
            !e.target.closest('.flickity-prev-next-button')
          ) {
            this.closeLightbox();
          }
        };
        document.addEventListener('click', this._onLightboxClose);

        // Escape key closes lightbox
        this._onKeydown = (e) => {
          if (e.key === 'Escape') {
            const lightbox = this._getLightbox();
            if (lightbox && lightbox.getAttribute('aria-hidden') === 'false') {
              this.closeLightbox();
            }
          }
        };
        document.addEventListener('keydown', this._onKeydown);

        // Re-layout carousels on viewport resize (responsive width change, mobile
        // emulation). Flickity's built-in resize repositions but doesn't re-measure
        // cells, so the slider width can stay stale until a reload — reloadCells fixes it.
        let resizeTimer;
        this._onResize = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.relayout(), 200);
        };
        window.addEventListener('resize', this._onResize);

        // Unmute auto-started extra-media videos on the user's gesture. Sound on
        // auto-started playback is blocked, but unmuting a playing video during a
        // click/tap is allowed — so the same gesture that selects the slide (or any
        // later click) turns sound on. Runs in bubble phase, after Flickity has
        // started the video. Skips the volume button (its own handler manages it).
        this._onExtraMediaUnmute = (e) => {
          if (e.target && e.target.closest && e.target.closest('[data-em-volume]')) return;
          // document scope: the lightbox (and its video) is moved to <body> when open.
          document.querySelectorAll('deferred-media[data-em-video] video').forEach((video) => {
            if (video._emAutoMuted && !video.paused) {
              video.muted = false;
              video._emAutoMuted = false;
            }
          });
        };
        document.addEventListener('click', this._onExtraMediaUnmute);
        document.addEventListener('pointerup', this._onExtraMediaUnmute);
      }

      removeEventListeners() {
        if (this._onZoomClick) this.removeEventListener('click', this._onZoomClick);
        if (this._onLightboxClose) document.removeEventListener('click', this._onLightboxClose);
        if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
        if (this._onTrapFocus) document.removeEventListener('keydown', this._onTrapFocus);
        if (this._reconcileCells) window.removeEventListener('load', this._reconcileCells);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._onExtraMediaUnmute) {
          document.removeEventListener('click', this._onExtraMediaUnmute);
          document.removeEventListener('pointerup', this._onExtraMediaUnmute);
        }
      }

      /* ============================
         Variant Change Handling
         ============================ */

      handleVariantChange(html, variantFeaturedMediaId) {
        console.log('DEBUG handleVariantChange called, mode:', this.galleryMode);
        if (this.galleryMode === 'per-color') {
          this.handlePerColorVariantChange(html);
        } else {
          this.handleStandardVariantChange(html, variantFeaturedMediaId);
        }
      }

      handlePerColorVariantChange(html) {
        const newGallery = html.querySelector('pdp-gallery');
        console.log('DEBUG perColor: newGallery found?', !!newGallery);
        if (!newGallery) return;

        const newColorId = newGallery.dataset.currentColorId;

        console.log('DEBUG perColor: newColorId =', newColorId, '| currentColorId =', this.currentColorId);
        // If color hasn't changed (size change, etc.) — leave gallery untouched
        if (newColorId === this.currentColorId) {
          console.log('DEBUG perColor: same color, skipping');
          return;
        }

        // Color changed — rebuild entire gallery
        this.currentColorId = newColorId;
        this.dataset.currentColorId = newColorId;
        this.rebuildGallery(newGallery);
      }

      handleStandardVariantChange(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId || !this.mainFlkty) return;
        const targetSlide = this.querySelector(
          `.pdp-gallery__main [data-media-id="${this.sectionId}-${variantFeaturedMediaId}"]`
        );
        if (targetSlide) {
          const index = parseInt(targetSlide.dataset.index, 10);
          this.mainFlkty.select(index);
        }
      }

      rebuildGallery(newGalleryElement) {
        // Close lightbox if open (prevents stale UI after rebuild)
        this.closeLightbox();

        this._destroyFlickityInstances();

        // Replace content
        const newMain = newGalleryElement.querySelector('.pdp-gallery__main');
        const newThumbs = newGalleryElement.querySelector('.pdp-gallery__thumbnails');
        const newLightboxCarousel = newGalleryElement.querySelector('.pdp-lightbox__carousel');

        const mainEl = this.querySelector('.pdp-gallery__main');
        const thumbEl = this.querySelector('.pdp-gallery__thumbnails');
        const lightbox = this._getLightbox();
        const lightboxCarouselEl = lightbox ? lightbox.querySelector('.pdp-lightbox__carousel') : null;

        if (mainEl && newMain) mainEl.innerHTML = newMain.innerHTML;
        if (thumbEl && newThumbs) thumbEl.innerHTML = newThumbs.innerHTML;
        if (lightboxCarouselEl && newLightboxCarousel) {
          lightboxCarouselEl.innerHTML = newLightboxCarousel.innerHTML;
        }

        // Update single-image attribute
        const isSingle = newGalleryElement.dataset.singleImage === 'true';
        if (isSingle) {
          this.dataset.singleImage = 'true';
        } else {
          delete this.dataset.singleImage;
        }
        // Sync the thumbnail strip's force-hide with the new color's single/multi state
        if (thumbEl) thumbEl.classList.toggle('hidden!', isSingle);

        // Re-initialize
        this.initMain();
        this.initThumbnails();
        // Autoplay the active slide's video after a color rebuild (a user action, not a page load)
        this.autoplaySelectedExtraMedia();
        // Lightbox re-inits lazily on next open
      }
    }
  );
}
